import { useEffect, useState } from "react";
import api from "../lib/api";
import { formatIDR } from "../lib/format";
import { toast } from "sonner";
import { Gift, Save, Power, Search, TrendingUp, Users, Coins, ChevronDown, ChevronUp, Filter } from "lucide-react";

export default function AdminReferral() {
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [c, s, h] = await Promise.all([
        api.get("/admin/referral/config"),
        api.get("/admin/referral/stats"),
        api.get("/admin/referral/history", { params: { limit: 200 } }),
      ]);
      setCfg(c.data);
      setStats(s.data);
      setEvents(h.data);
    } catch (e) {
      toast.error("Gagal memuat data referal");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      await api.post("/admin/referral/config", {
        enabled: cfg.enabled,
        tier1_min: Number(cfg.tier1_min) || 0,
        tier1_bonus: Number(cfg.tier1_bonus) || 0,
        tier2_min: Number(cfg.tier2_min) || 0,
        tier2_bonus: Number(cfg.tier2_bonus) || 0,
        monthly_cap: Number(cfg.monthly_cap) || 0,
      });
      toast.success("Konfigurasi referal disimpan");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async () => {
    try {
      const r = await api.post("/admin/referral/toggle");
      setCfg((c) => ({ ...(c || {}), enabled: r.data.enabled }));
      toast.success(`Program referal ${r.data.enabled ? "diaktifkan" : "dinonaktifkan"}`);
    } catch (e) {
      toast.error("Gagal toggle referal");
    }
  };

  if (busy) return <div className="text-white/50 text-center py-12">Memuat...</div>;
  if (!cfg) return <div className="text-white/50 text-center py-12">Gagal memuat.</div>;

  const filtered = events.filter((e) => {
    if (statusFilter && e.status !== statusFilter) return false;
    if (q) {
      const ql = q.toLowerCase();
      return (
        (e.referrer_name || "").toLowerCase().includes(ql) ||
        (e.referrer_email || "").toLowerCase().includes(ql) ||
        (e.referee_name || "").toLowerCase().includes(ql) ||
        (e.referee_email || "").toLowerCase().includes(ql)
      );
    }
    return true;
  });

  return (
    <div data-testid="admin-referral-tab" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display font-black text-2xl text-white flex items-center gap-2">
          <Gift className="w-6 h-6 text-[#00F0FF]" /> Program Referal
        </h2>
        <button
          data-testid="referral-toggle-btn"
          onClick={toggle}
          className={`px-4 py-2 text-xs uppercase font-bold tracking-widest flex items-center gap-2 ${
            cfg.enabled ? "bg-[#00FF66]/15 border border-[#00FF66]/50 text-[#00FF66]" : "bg-white/5 border border-white/15 text-white/50"
          }`}
        >
          <Power className="w-3.5 h-3.5" />
          {cfg.enabled ? "AKTIF" : "NONAKTIF"}
        </button>
      </div>

      {/* Statistik */}
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="border border-white/10 p-4" data-testid="admin-stat-bonus">
          <Coins className="w-4 h-4 text-[#00FF66] mb-2" />
          <div className="font-display font-black text-2xl text-[#00FF66]">{formatIDR(stats?.total_bonus_paid || 0)}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Total Bonus Cair (Kedua Member)</div>
        </div>
        <div className="border border-white/10 p-4" data-testid="admin-stat-events">
          <TrendingUp className="w-4 h-4 text-[#00F0FF] mb-2" />
          <div className="font-display font-black text-2xl text-white">{stats?.total_bonus_events || 0}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Event Bonus</div>
        </div>
        <div className="border border-white/10 p-4" data-testid="admin-stat-signups">
          <Users className="w-4 h-4 text-[#7C6EFF] mb-2" />
          <div className="font-display font-black text-2xl text-white">{stats?.total_referred_signups || 0}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">Pendaftaran Referal</div>
        </div>
      </div>

      {/* Konfigurasi */}
      <div className="border border-white/10 p-5">
        <h3 className="font-display font-bold text-lg text-white mb-4">Konfigurasi Bonus</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/60">Tier 1: Belanja min. (Rp)</label>
            <input
              data-testid="cfg-tier1-min"
              type="number"
              value={cfg.tier1_min ?? 0}
              onChange={(e) => setCfg({ ...cfg, tier1_min: e.target.value })}
              className="w-full bg-black border border-white/15 focus:border-[#00F0FF] text-white px-3 py-2 mt-1"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/60">Tier 1: Bonus (Rp)</label>
            <input
              data-testid="cfg-tier1-bonus"
              type="number"
              value={cfg.tier1_bonus ?? 0}
              onChange={(e) => setCfg({ ...cfg, tier1_bonus: e.target.value })}
              className="w-full bg-black border border-white/15 focus:border-[#00F0FF] text-white px-3 py-2 mt-1"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/60">Tier 2: Belanja min. (Rp)</label>
            <input
              data-testid="cfg-tier2-min"
              type="number"
              value={cfg.tier2_min ?? 0}
              onChange={(e) => setCfg({ ...cfg, tier2_min: e.target.value })}
              className="w-full bg-black border border-white/15 focus:border-[#00F0FF] text-white px-3 py-2 mt-1"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/60">Tier 2: Bonus (Rp)</label>
            <input
              data-testid="cfg-tier2-bonus"
              type="number"
              value={cfg.tier2_bonus ?? 0}
              onChange={(e) => setCfg({ ...cfg, tier2_bonus: e.target.value })}
              className="w-full bg-black border border-white/15 focus:border-[#00F0FF] text-white px-3 py-2 mt-1"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/60">Limit Bulanan per Referrer (Rp)</label>
            <input
              data-testid="cfg-monthly-cap"
              type="number"
              value={cfg.monthly_cap ?? 0}
              onChange={(e) => setCfg({ ...cfg, monthly_cap: e.target.value })}
              className="w-full bg-black border border-white/15 focus:border-[#00F0FF] text-white px-3 py-2 mt-1"
            />
            <p className="text-[10px] text-white/40 mt-1">Bonus di atas limit akan dicatat sebagai &quot;capped&quot; (tidak masuk saldo).</p>
          </div>
        </div>
        <button
          data-testid="cfg-save-btn"
          onClick={save}
          disabled={saving}
          className="btn-primary mt-5 disabled:opacity-50 flex items-center gap-2"
        >
          <Save className="w-4 h-4" /> {saving ? "Menyimpan..." : "Simpan Konfigurasi"}
        </button>
      </div>

      {/* Riwayat */}
      <div className="border border-white/10 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="font-display font-bold text-lg text-white">Riwayat Bonus</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
              <input
                data-testid="history-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cari nama/email..."
                className="bg-black border border-white/15 focus:border-[#00F0FF] text-white text-xs pl-8 pr-3 py-2 w-56"
              />
            </div>
            <button
              data-testid="history-filter-toggle"
              onClick={() => setShowFilters((v) => !v)}
              className="px-3 py-2 border border-white/15 hover:border-[#00F0FF] text-white/70 hover:text-[#00F0FF] text-xs uppercase font-bold tracking-widest flex items-center gap-1"
            >
              <Filter className="w-3.5 h-3.5" /> Filter
              {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="flex items-center gap-2 mb-4 text-xs" data-testid="history-filters">
            <span className="text-white/50 uppercase tracking-widest">Status:</span>
            {["", "paid", "capped"].map((s) => (
              <button
                key={s || "all"}
                onClick={() => setStatusFilter(s)}
                data-testid={`filter-status-${s || "all"}`}
                className={`px-3 py-1.5 border uppercase tracking-widest ${
                  statusFilter === s ? "border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/15 text-white/60 hover:border-white/30"
                }`}
              >
                {s || "Semua"}
              </button>
            ))}
          </div>
        )}

        {filtered.length === 0 ? (
          <p className="text-white/50 text-sm" data-testid="history-empty">Belum ada event bonus.</p>
        ) : (
          <div className="space-y-2 max-h-[480px] overflow-y-auto" data-testid="history-list">
            {filtered.map((e, i) => (
              <div key={e.id || i} className="flex items-center gap-3 border-b border-white/5 pb-2">
                <Coins className={`w-4 h-4 shrink-0 ${e.status === "paid" ? "text-[#00FF66]" : "text-white/40"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate">
                    <span className="font-bold">{e.referrer_name || e.referrer_email || "?"}</span>
                    <span className="text-white/40"> ← {e.referee_name || e.referee_email || "Teman"}</span>
                  </p>
                  <p className="text-[10px] text-white/40">
                    {e.created_at ? new Date(e.created_at).toLocaleString("id-ID") : ""}
                    {e.status === "capped" ? " · capped (limit tercapai)" : ""}
                     {e.status === "paid" && e.referee_amount ? ` · masing-masing ${formatIDR(e.amount || 0)}` : ""}
                    {e.month_key ? ` · ${e.month_key}` : ""}
                  </p>
                </div>
                <p className={`font-bold text-sm ${e.status === "paid" ? "text-[#00FF66]" : "text-white/40"}`}>
                  {e.status === "paid" ? "+" : ""}
                  {formatIDR(e.total_amount || e.amount || 0)}
                </p>
              </div>
            ))}
          </div>
        )}
        <p className="text-[10px] text-white/40 mt-3">
          Menampilkan {filtered.length} dari {events.length} event.
        </p>
      </div>
    </div>
  );
}
